import { JOB_SERVICES_ENDPOINT } from "../../../../consts";
import { useCRUD } from "../../../../api/crud";
import { useParams } from "react-router-dom";
import { ReactNode, useEffect, useState } from "react";
import CustomCircularProgress from "../../../../components/ui/customCircularProgress/customCircularProgress.component";
import { Box, Typography, Divider, Chip } from "@mui/material";
import { basicInfoContainerStyle } from "../../../../styles/commonStyles";
import { useTranslation } from "react-i18next";
import { useHasPermission } from "../../../../contexts/auth";
import { formatDateTime } from "../../../../utils";
import { useBreadcrumbs } from "../../../../components/ui/breadcrumps/breadcrumbsContext";

type DetailRowProps = {
  label: string;
  children: ReactNode;
  noBorder?: boolean;
};

const DetailRow = ({ label, children, noBorder = false }: DetailRowProps) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", sm: "200px 1fr" },
      gap: { xs: 0.5, sm: 2 },
      alignItems: "start",
      py: 1.25,
      px: 1.5,
      borderBottom: noBorder ? "none" : "1px solid",
      borderColor: "divider",
    }}
  >
    <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 600 }}>
      {label}
    </Typography>
    <Box sx={{ minHeight: "24px", display: "flex", alignItems: "center" }}>{children}</Box>
  </Box>
);

export const JobServiceCatalogBasicInfo = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { get } = useCRUD(JOB_SERVICES_ENDPOINT);
  const { setOverride } = useBreadcrumbs();
  const [jobService, setJobService] = useState<any | null>(null);
  const canViewJobServices = useHasPermission("service.view_jobservice");

  useEffect(() => {
    const fetchJobService = async () => {
      if (id) {
        const response = await get(id);
        if (response.data) {
          setJobService(response.data);
          setOverride(`/service/services-catalog/${id}`, response.data.title || id);
        }
      }
    };

    const handleJobServiceUpdated = () => {
      fetchJobService();
    };

    window.addEventListener("jobServiceCatalogUpdated", handleJobServiceUpdated);
    fetchJobService();
    return () => {
      window.removeEventListener("jobServiceCatalogUpdated", handleJobServiceUpdated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!canViewJobServices) {
    return null;
  }

  if (!jobService) return <CustomCircularProgress />;

  return (
    <Box sx={basicInfoContainerStyle}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
          {jobService.title}
        </Typography>
      </Box>

      <Divider />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          {t("Basic Information")}
        </Typography>
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          <DetailRow label={t("Title")}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              {jobService.title && (
                <Chip
                  label={jobService.title}
                  size="small"
                  sx={
                    jobService.color
                      ? {
                          bgcolor: jobService.color,
                          color: "white",
                          "& .MuiChip-label": { color: "white" },
                        }
                      : {
                          bgcolor: "rgba(128, 128, 128, 0.25)",
                          color: "text.secondary",
                        }
                  }
                />
              )}
              {!jobService.title && (
                <Typography variant="subtitle1">-</Typography>
              )}
            </Box>
          </DetailRow>
          {jobService.description && (
            <DetailRow label={t("Description")} noBorder>
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: "pre-wrap",
                  color: "text.secondary",
                }}
              >
                {jobService.description}
              </Typography>
            </DetailRow>
          )}
        </Box>
      </Box>

      <Divider />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          {t("Metadata")}
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>{t("Created At")}:</strong> {formatDateTime(jobService.created_at)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>{t("Updated At")}:</strong> {formatDateTime(jobService.updated_at)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
